import { TutorialConfig, SoftwareType } from './types';
import { freecadTutorialConfig } from './configs/freecad';
import { blenderTutorialConfig } from './configs/blender';
import { musescoreTutorialConfig } from './configs/musescore';
import { kicadTutorialConfig } from './configs/kicad';
import { odooTutorialConfig } from './configs/odoo';
import { immichTutorialConfig } from './configs/immich';
import { audacityTutorialConfig } from './configs/audacity';
import { shotcutTutorialConfig } from './configs/shotcut';
import { gimpTutorialConfig } from './configs/gimp';
import { libreofficeTutorialConfig } from './configs/libreoffice';
import { inkscapeTutorialConfig } from './configs/inkscape';

// Tutorial Registry - Add new software tutorials here
const tutorialRegistry = new Map<SoftwareType, TutorialConfig>([
  ['audacity', audacityTutorialConfig],
  ['shotcut', shotcutTutorialConfig],
  ['gimp', gimpTutorialConfig],
  ['libreoffice', libreofficeTutorialConfig],
  ['inkscape', inkscapeTutorialConfig],
  ['freecad', freecadTutorialConfig],
  ['blender', blenderTutorialConfig],
  ['musescore', musescoreTutorialConfig],
  ['kicad', kicadTutorialConfig],
  ['odoo', odooTutorialConfig],
  ['immich', immichTutorialConfig],
]);

// Helper functions for tutorial management
export const getTutorialConfig = (software: SoftwareType): TutorialConfig | undefined => {
  return tutorialRegistry.get(software);
};

export const getAllTutorialConfigs = (): TutorialConfig[] => {
  return Array.from(tutorialRegistry.values());
};

export const isSupportedSoftware = (software: string): boolean => {
  return tutorialRegistry.has(software as SoftwareType);
};

export const getSupportedSoftware = (): SoftwareType[] => {
  return Array.from(tutorialRegistry.keys());
};

// Determine which tutorial to use based on user permissions
export const getTutorialForUser = (permissions: string[]): TutorialConfig | undefined => {
  console.debug('[TutorialRegistry] Resolving tutorial for permissions:', permissions);
  
  // Check if user has label permissions for specific software
  const hasBlenderPermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('blender') || 
      p.toLowerCase().includes('habitat.blender')
    )
  );
  const hasFreecadPermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('freecad') || 
      p.toLowerCase().includes('habitat.freecad')
    )
  );
  const hasImmichPermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('immich') || 
      p.toLowerCase().includes('habitat.immich')
    )
  );
  const hasMusescorePermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('musescore') || 
      p.toLowerCase().includes('habitat.musescore')
    )
  );
  const hasKicadPermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('kicad') || 
      p.toLowerCase().includes('habitat.kicad')
    )
  );
  const hasOdooPermission = permissions.some(p => 
    p.toLowerCase().startsWith('label_') && (
      p.toLowerCase().includes('odoo') || 
      p.toLowerCase().includes('habitat.odoo')
    )
  );
  const hasShotcutPermission = permissions.some((p) => {
    const lower = p.toLowerCase();
    if (!lower.startsWith('label_')) {
      return false;
    }

    const match = (
      lower.includes('shotcut') ||
      lower.includes('habitat.shotcut')
    );

    if (match) {
      console.debug('[TutorialRegistry] Matched Shotcut permission:', p);
    }

    return match;
  });
  const hasAudacityPermission = permissions.some((p) => {
    const lower = p.toLowerCase();
    if (!lower.startsWith('label_')) {
      return false;
    }

    const match = (
      lower.includes('audacity') ||
      lower.includes('habitat.audacity') ||
      lower.includes('audacity_project') ||
      lower.includes('audacity.label')
    );

    if (match) {
      console.debug('[TutorialRegistry] Matched Audacity permission:', p);
    }

    return match;
  });
  const hasGimpPermission = permissions.some((p) => {
    const lower = p.toLowerCase();
    if (!lower.startsWith('label_')) {
      return false;
    }

    const match = (
      lower.includes('gimp') ||
      lower.includes('habitat.gimp')
    );

    if (match) {
      console.debug('[TutorialRegistry] Matched GIMP permission:', p);
    }

    return match;
  });
  const hasLibreOfficePermission = permissions.some((p) => {
    const lower = p.toLowerCase();
    if (!lower.startsWith('label_')) {
      return false;
    }

    const match = (
      lower.includes('libreoffice') ||
      lower.includes('habitat.libreoffice')
    );

    if (match) {
      console.debug('[TutorialRegistry] Matched LibreOffice permission:', p);
    }

    return match;
  });
  const hasInkscapePermission = permissions.some((p) => {
    const lower = p.toLowerCase();
    if (!lower.startsWith('label_')) {
      return false;
    }

    const match = (
      lower.includes('inkscape') ||
      lower.includes('habitat.inkscape')
    );

    if (match) {
      console.debug('[TutorialRegistry] Matched Inkscape permission:', p);
    }

    return match;
  });


  // Priority order: Shotcut > Audacity > GIMP > LibreOffice > Inkscape > Odoo > KiCad > MuseScore > Blender > FreeCAD > Immich
  // Return the highest priority tutorial that the user has permission for

  if (hasShotcutPermission) {
    console.debug('[TutorialRegistry] Selected Shotcut tutorial');
    return tutorialRegistry.get('shotcut');
  }

  if (hasAudacityPermission) {
    console.debug('[TutorialRegistry] Selected Audacity tutorial');
    return tutorialRegistry.get('audacity');
  }

  if (hasGimpPermission) {
    console.debug('[TutorialRegistry] Selected GIMP tutorial');
    return tutorialRegistry.get('gimp');
  }

  if (hasLibreOfficePermission) {
    console.debug('[TutorialRegistry] Selected LibreOffice tutorial');
    return tutorialRegistry.get('libreoffice');
  }

  if (hasInkscapePermission) {
    console.debug('[TutorialRegistry] Selected Inkscape tutorial');
    return tutorialRegistry.get('inkscape');
  }

  if (hasOdooPermission) {
    return tutorialRegistry.get('odoo');
  }
  
  if (hasKicadPermission) {
    return tutorialRegistry.get('kicad');
  }
  
  if (hasMusescorePermission) {
    return tutorialRegistry.get('musescore');
  }
  
  if (hasBlenderPermission) {
    return tutorialRegistry.get('blender');
  }
  
  if (hasFreecadPermission) {
    return tutorialRegistry.get('freecad');
  }
  
  if (hasImmichPermission) {
    return tutorialRegistry.get('immich');
  }
  
  // No matching tutorial found
  console.debug('[TutorialRegistry] No tutorial mapping found for permissions');
  return undefined;
};

// Register a new tutorial (for extensibility)
export const registerTutorial = (config: TutorialConfig): void => {
  tutorialRegistry.set(config.software, config);
};
